/**
 * Static page registry for the command palette's Pages group. Mirrors the
 * sidebar nav (apps/web/src/components/layout/sidebar.tsx). Pure data +
 * pure helpers — node-env testable.
 */

export interface PageEntry {
  id: string;
  label: string;
  path: (slug: string) => string;
  keywords: string[];
  brandScoped: boolean;
}

export const PAGE_REGISTRY: PageEntry[] = [
  // Global
  { id: "dashboard", label: "Dashboard", path: () => "/dashboard", keywords: ["home", "brands"], brandScoped: false },
  { id: "settings-notifications", label: "Notification Settings", path: () => "/settings/notifications", keywords: ["email", "telegram", "alerts"], brandScoped: false },
  // Content
  { id: "compose", label: "Compose (AI)", path: (s) => `/${s}/compose`, keywords: ["ai", "create", "generate"], brandScoped: true },
  { id: "quick-post", label: "Quick Post", path: (s) => `/${s}/quick-post`, keywords: ["publish", "new post", "post now", "schedule"], brandScoped: true },
  { id: "post-builder", label: "Post Builder", path: (s) => `/${s}/quick-post-pro`, keywords: ["template", "platform", "advanced"], brandScoped: true },
  { id: "projects", label: "Projects", path: (s) => `/${s}`, keywords: ["pipeline", "content"], brandScoped: true },
  { id: "ideas", label: "Ideas", path: (s) => `/${s}/ideas`, keywords: ["brainstorm", "backlog"], brandScoped: true },
  { id: "review", label: "Review Queue", path: (s) => `/${s}/review`, keywords: ["approve", "pending"], brandScoped: true },
  { id: "calendar", label: "Calendar", path: (s) => `/${s}/calendar`, keywords: ["schedule", "posts"], brandScoped: true },
  { id: "library", label: "Library", path: (s) => `/${s}/library`, keywords: ["assets", "media", "images"], brandScoped: true },
  { id: "inbox-dms", label: "Inbox · DMs", path: (s) => `/${s}/inbox/dms`, keywords: ["messages", "conversations"], brandScoped: true },
  { id: "inbox-comments", label: "Inbox · Comments", path: (s) => `/${s}/inbox/comments`, keywords: ["replies", "engagement"], brandScoped: true },
  { id: "activity", label: "Activity", path: (s) => `/${s}/activity`, keywords: ["log", "history", "events", "feed"], brandScoped: true },
  // Analytics
  { id: "analytics", label: "Analytics", path: (s) => `/${s}/analytics`, keywords: ["metrics", "performance", "stats"], brandScoped: true },
  { id: "analytics-instagram", label: "Instagram Analytics", path: (s) => `/${s}/analytics/instagram`, keywords: ["ig", "followers"], brandScoped: true },
  { id: "ads", label: "Ads", path: (s) => `/${s}/ads`, keywords: ["boost", "paid", "amplify"], brandScoped: true },
  // Settings
  { id: "settings-brand", label: "Brand Settings", path: (s) => `/${s}/settings/brand`, keywords: ["name", "workspace"], brandScoped: true },
  { id: "settings-kit", label: "Brand Kit", path: (s) => `/${s}/settings/kit`, keywords: ["colors", "fonts", "voice", "logo"], brandScoped: true },
  { id: "settings-ai", label: "AI Config", path: (s) => `/${s}/settings/ai`, keywords: ["models", "providers"], brandScoped: true },
  { id: "settings-ai-prompts", label: "AI Prompts", path: (s) => `/${s}/settings/ai-prompts`, keywords: ["templates", "prompts"], brandScoped: true },
  { id: "settings-scheduling", label: "Scheduling Settings", path: (s) => `/${s}/settings/scheduling`, keywords: ["timezone", "slots"], brandScoped: true },
  { id: "settings-automation", label: "Automation Settings", path: (s) => `/${s}/settings/automation`, keywords: ["autopilot", "auto-schedule"], brandScoped: true },
  { id: "settings-ai-usage", label: "AI Usage", path: (s) => `/${s}/settings/ai-usage`, keywords: ["budget", "costs", "spend"], brandScoped: true },
  { id: "settings-team", label: "Team", path: (s) => `/${s}/settings/team`, keywords: ["members", "roles", "invite"], brandScoped: true },
  { id: "settings-accounts", label: "Social Accounts", path: (s) => `/${s}/settings/accounts`, keywords: ["instagram", "tiktok", "connect"], brandScoped: true },
  { id: "settings-inbox", label: "Inbox Settings", path: (s) => `/${s}/settings/inbox`, keywords: ["routing", "auto-reply"], brandScoped: true },
];

/**
 * Strips `/${slug}` from the pathname and returns the sub-route ONLY if it
 * exactly matches a registry path (no residual params) — so a brand switch
 * can land on the same page (`/acme/calendar` → `calendar`) but never on a
 * foreign entity (`/acme/projects/abc` → null → brand root).
 */
export function subRouteOf(pathname: string, slug: string): string | null {
  const prefix = `/${slug}`;
  if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) return null;
  const sub = pathname.slice(prefix.length).replace(/^\//, "").replace(/\/$/, "");
  if (!sub) return null; // brand root — nothing to preserve
  for (const entry of PAGE_REGISTRY) {
    if (entry.brandScoped && entry.path(slug) === `${prefix}/${sub}`) return sub;
  }
  return null;
}
